/**
 * Canonical route document shared by the planner, the API and the phone app.
 *
 * Versioned deliberately: routes are persisted in D1 and cached on devices, so
 * a change here needs a migration, not a rename.
 */

import type { SpeedRun } from './speed.js';

export const ROUTE_SCHEMA = 'cad.route/1' as const;

/** GeoJSON axis order: [longitude, latitude]. Used everywhere internally. */
export type LngLat = [number, number];

/** [west, south, east, north] */
export type BBox = [number, number, number, number];

/**
 * `stop` is somewhere you actually pull over: it splits the route into legs and
 * shows in the app's itinerary. `via` only shapes the line — it exists to drag
 * the route onto a particular road and is invisible while driving.
 */
export type WaypointKind = 'stop' | 'via';

export interface Waypoint {
  /** Stable across edits so the planner can diff and re-route only what moved. */
  id: string;
  lng: number;
  lat: number;
  kind: WaypointKind;
  name?: string;
}

export type RoutingProfile = 'driving' | 'driving-traffic';

/**
 * What every route is planned with.
 *
 * `driving` gives each road a fixed speed from its type and limit and knows
 * nothing about what traffic actually does, which made it confidently wrong
 * on ordinary country A-roads: a Norwich–Barnham run went in and out of
 * Thetford where Google, and every driver, takes the A1088 through Euston.
 * `driving-traffic` routes on speeds actually observed — typical for the time
 * of day and week, and live — and picked Google's road, and Google's second
 * choice as its alternative. Same price per request, same 25-waypoint limit,
 * and it quietly falls back to `driving` where Mapbox has no traffic data.
 *
 * `driving` stays in the type because routes saved before the switch carry it.
 */
export const DEFAULT_PROFILE: RoutingProfile = 'driving-traffic';

export interface RouteOptions {
  avoidTolls: boolean;
  avoidFerries: boolean;
  avoidMotorways: boolean;
}

export const DEFAULT_ROUTE_OPTIONS: RouteOptions = {
  avoidTolls: false,
  avoidFerries: false,
  avoidMotorways: false,
};

/** One stop-to-stop section, for the itinerary view. */
export interface RouteLeg {
  distance: number;
  duration: number;
  fromWaypointId: string;
  toWaypointId: string;
}

/**
 * One instruction from the routing engine, placed on the route.
 *
 * `at` is metres along the whole route rather than a coordinate, because that
 * is what the app already knows about itself every second — comparing two
 * numbers beats searching geometry for the nearest turn on every fix.
 *
 * The wording is Mapbox's own. Composing "turn left onto Bell Lane" from a type
 * and a modifier is easy to do badly — roundabout exits, forks, motorway merges
 * and slip roads each read differently — and theirs is already written and
 * already right.
 */
export interface Maneuver {
  /** Metres from the start of the route. */
  at: number;
  /** 'turn', 'roundabout', 'merge', 'fork', 'arrive', and so on. */
  type: string;
  /** 'left', 'slight right', 'straight' — absent for types that don't turn. */
  modifier?: string;
  /** The road being joined, where the engine names one. */
  name?: string;
  /** Ready-to-read text, e.g. "Turn left onto Bell Lane". */
  instruction: string;
}

export interface RouteDoc {
  schema: typeof ROUTE_SCHEMA;
  id: string;
  name: string;
  notes?: string;
  profile: RoutingProfile;
  options: RouteOptions;
  waypoints: Waypoint[];
  /**
   * Full snapped road geometry, encoded polyline at precision 6.
   *
   * Stored rather than recomputed so a route renders identically forever and
   * the phone needs no routing calls at all. This is also what makes offline
   * mode nearly free later.
   */
  geometry: string;
  /** metres */
  distance: number;
  /** seconds — traffic-aware for driving-traffic, free-flow for older `driving` routes */
  duration: number;
  legs: RouteLeg[];
  bbox: BBox;
  /**
   * Speed limit per geometry segment, run-length encoded, in km/h.
   *
   * Optional because routes saved before this existed don't have it, and a
   * missing limit is a normal state anyway — plenty of roads aren't signed in
   * the map data.
   */
  speedLimits?: SpeedRun[];
  /**
   * Where the route turns, and what it says to do. Optional: routes saved
   * before this existed have none, and the app simply shows no turn prompt.
   */
  maneuvers?: Maneuver[];
  /** Where it starts and ends, in words. Absent when the lookup found nothing. */
  startName?: string;
  endName?: string;
  createdAt: number;
  updatedAt: number;
  /** Bumped on every save; lets the app tell a cached copy is stale. */
  revision: number;
}

/** What the planner holds before a route has been saved or costed. */
export type DraftRoute = Pick<RouteDoc, 'name' | 'notes' | 'profile' | 'options' | 'waypoints'>;

export function isRouteDoc(value: unknown): value is RouteDoc {
  if (typeof value !== 'object' || value === null) return false;
  const d = value as Record<string, unknown>;
  return (
    d.schema === ROUTE_SCHEMA &&
    typeof d.id === 'string' &&
    typeof d.geometry === 'string' &&
    Array.isArray(d.waypoints)
  );
}
